/**
 * RecordTimelinePanel — the unified, provenance-stamped activity stream +
 * Time Machine scrubber for one account record.
 *
 * WHY: the auditor flagged (a) the record timeline was passive and
 * single-source, and (b) Time Machine existed only as a backend module with
 * no UI. This panel is the THIN view over two pure engines:
 *
 *   • src/lib/crm/unifiedTimeline.ts merges every silo — emails, meetings,
 *     tasks, tickets, notes, field changes, agent actions — into one stream
 *     with per-source filter chips and inline provenance/ledger-seal badges
 *     on agent entries;
 *   • src/lib/crm/timeMachineModel.ts replays the record's field-change
 *     history so a slider can scrub to any past state, with "Restore this
 *     state" and "Revert agent changes only" compiling governed proposals.
 *
 * NOTHING here writes records directly. Both revert buttons queue
 * `AgentAction` proposals through the EXISTING approvals pipeline
 * (queueApproval → operator sign-off → executor), exactly per the
 * reversal.ts contract: an undo is just another governed proposal. Field
 * changes are derived only from REAL history (the account audit trail +
 * the sealed Decision Ledger) — never fabricated.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Bot,
  Clock,
  History,
  ListTodo,
  Mail,
  PenLine,
  ShieldCheck,
  StickyNote,
  Ticket as TicketIcon,
  Undo2,
  Video,
} from 'lucide-react';
import { C } from '../lib/tokens';
import { useToast } from './ToastProvider';
import { subscribeActivity } from '../lib/db/activity';
import { subscribeTasks } from '../lib/db/tasks';
import { subscribeTickets } from '../lib/db/tickets';
import { queueApproval } from '../lib/agent/approvals';
import { REVERSAL_NOTE_PREFIX } from '../lib/agent/reversal';
import {
  filterTimeline,
  groupByDay,
  lastTouch,
  mergeTimeline,
  nextScheduled,
  type TimelineEntry,
  type TimelineSource,
} from '../lib/crm/unifiedTimeline';
import {
  agentOnlyRevert,
  diffStates,
  replayTo,
  revertPlan,
  sealCoverage,
  snapshotsAt,
  type FieldChange,
  type RecordState,
} from '../lib/crm/timeMachineModel';
import {
  PIPELINE_STAGES,
  type Account,
  type ActivityEntry,
  type PipelineStage,
  type ProjectTask,
  type Ticket,
} from '../lib/db/types';
import type { AgentAction } from '../lib/agent/types';

// ---------------------------------------------------------------------------
// Pure adapters: real silos → engine inputs (exported for reuse; no IO)
// ---------------------------------------------------------------------------

/** Ledger origins that mean "an agent did this". */
const AGENT_SOURCES = new Set(['agent', 'scheduler', 'autopilot']);

// The EXACT summary shapes the approvals pipeline seals into the ledger
// (summarizeAction verb — detail). Parsing these recovers structured
// field changes without inventing a parallel history store.
const LEDGER_EDIT_RE = /^Edit field — (.+?) · (.+?): (.*?) → (.*)$/;
const LEDGER_MOVE_RE = /^Advance workflow — (.+?) · "(.+?)": (.+?) → (.+)$/;
// The account audit-trail shapes (accounts.ts / AccountDetailModal).
const AUDIT_MOVE_RE = /^Moved workflow "(.+?)": (.+?) → (.+)$/;
const AUDIT_STAGE_RE = /^Workflow stage changed — "(.+?)": (.+?) → (.+)$/;

const WORKFLOW_ATTR_RE = /^workflow "(.+)" stage$/;
const workflowAttr = (name: string): string => `workflow "${name}" stage`;

/** Account fields the Time Machine can restore via update_record_field. */
const SCALAR_ATTRS: ReadonlyArray<keyof Account> = [
  'name',
  'pocName',
  'pocTitle',
  'pocEmail',
  'pocPhone',
  'ownerName',
  'assignedTo',
  'notes',
];

/** Structured field changes recovered from the account's REAL history. */
export function deriveFieldChanges(account: Account, ledger: ActivityEntry[]): FieldChange[] {
  const out: FieldChange[] = [];

  // Sealed ledger entries (executed writes touching this account by name).
  for (const e of ledger) {
    if (e.event !== 'executed') continue;
    const at = Date.parse(e.at);
    if (Number.isNaN(at)) continue;
    const actor: FieldChange['actor'] = AGENT_SOURCES.has(e.source)
      ? { kind: 'agent', id: `argo (${e.source})` }
      : { kind: 'human', id: e.source === 'chat' ? 'you (chat)' : e.source };
    const edit = LEDGER_EDIT_RE.exec(e.summary);
    if (edit && edit[1] === account.name) {
      out.push({
        attr: edit[2],
        from: edit[3] === '—' ? null : edit[3],
        to: edit[4],
        at,
        actor,
        ledgerSeal: e.hash,
      });
      continue;
    }
    const move = LEDGER_MOVE_RE.exec(e.summary);
    if (move && move[1] === account.name) {
      out.push({
        attr: workflowAttr(move[2]),
        from: move[3],
        to: move[4],
        at,
        actor,
        ledgerSeal: e.hash,
      });
    }
  }

  // Audit-trail stage moves (human edits in the modal + adapter writes).
  for (const a of account.audit) {
    const at = Date.parse(a.at);
    if (Number.isNaN(at)) continue;
    const m = AUDIT_MOVE_RE.exec(a.text) ?? AUDIT_STAGE_RE.exec(a.text);
    if (!m) continue;
    const isAgent = (a.actor ?? '').toLowerCase().includes('agent');
    out.push({
      attr: workflowAttr(m[1]),
      from: m[2],
      to: m[3],
      at,
      actor: { kind: isAgent ? 'agent' : 'human', id: a.actor ?? 'you' },
    });
  }

  // The same move can land in BOTH the ledger (sealed) and the audit trail
  // (the executor's adapter write). Collapse near-duplicates — same
  // attr/from/to within 5 minutes — keeping the sealed row.
  out.sort((a, b) => a.at - b.at);
  const FIVE_MIN = 5 * 60 * 1000;
  return out.filter((c, i) => {
    if (c.ledgerSeal) return true;
    return !out.some(
      (o, j) =>
        j !== i &&
        o.ledgerSeal &&
        o.attr === c.attr &&
        String(o.from) === String(c.from) &&
        String(o.to) === String(c.to) &&
        Math.abs(o.at - c.at) <= FIVE_MIN,
    );
  });
}

/** The record's CURRENT flattened state (scalars + per-workflow stages). */
export function accountState(account: Account): RecordState {
  const state: RecordState = {};
  for (const attr of SCALAR_ATTRS) state[attr] = account[attr] ?? null;
  for (const wf of account.workflows) state[workflowAttr(wf.name)] = wf.stage;
  return state;
}

/** The state BEFORE any recorded change: unapply each attr's first change. */
export function baseStateOf(current: RecordState, changes: FieldChange[]): RecordState {
  const base: RecordState = { ...current };
  const sorted = [...changes].sort((a, b) => a.at - b.at);
  const seen = new Set<string>();
  for (const c of sorted) {
    if (seen.has(c.attr)) continue;
    seen.add(c.attr);
    base[c.attr] = c.from;
  }
  return base;
}

/**
 * Compile a target-state diff into governed AgentActions. Workflow-stage
 * attrs become move_workflow_stage (validated against the real workflow +
 * stage list); scalar attrs become update_record_field. Anything else is
 * reported in `skipped` — shown honestly, never silently dropped.
 */
export function planToActions(
  changes: { attr: string; from: unknown; to: unknown }[],
  account: Account,
): { actions: AgentAction[]; skipped: string[] } {
  const actions: AgentAction[] = [];
  const skipped: string[] = [];
  for (const c of changes) {
    const wf = WORKFLOW_ATTR_RE.exec(c.attr);
    if (wf) {
      const workflow = account.workflows.find((w) => w.name === wf[1]);
      const toStage = String(c.to ?? '');
      if (workflow && (PIPELINE_STAGES as readonly string[]).includes(toStage)) {
        actions.push({
          kind: 'move_workflow_stage',
          accountId: account.id,
          accountName: account.name,
          workflowId: workflow.id,
          workflowName: workflow.name,
          fromStage: workflow.stage,
          toStage: toStage as PipelineStage,
        });
      } else {
        skipped.push(c.attr);
      }
      continue;
    }
    if ((SCALAR_ATTRS as readonly string[]).includes(c.attr)) {
      const from = account[c.attr as keyof Account];
      actions.push({
        kind: 'update_record_field',
        recordType: 'account',
        recordId: account.id,
        recordName: account.name,
        attr: c.attr,
        from: typeof from === 'string' && from !== '' ? from : null,
        to: String(c.to ?? ''),
      });
      continue;
    }
    skipped.push(c.attr);
  }
  return { actions, skipped };
}

// ---------------------------------------------------------------------------
// View helpers
// ---------------------------------------------------------------------------

const SOURCE_META: Record<TimelineSource, { label: string; icon: typeof Mail; tint: string }> = {
  email: { label: 'Emails', icon: Mail, tint: C.blueElectric },
  meeting: { label: 'Meetings', icon: Video, tint: C.magenta },
  task: { label: 'Tasks', icon: ListTodo, tint: C.green },
  ticket: { label: 'Tickets', icon: TicketIcon, tint: C.blueElectric },
  note: { label: 'Notes', icon: StickyNote, tint: C.magenta },
  agent: { label: 'Agent', icon: Bot, tint: C.green },
  'field-change': { label: 'Field changes', icon: PenLine, tint: C.blueElectric },
};

const ALL_SOURCES = Object.keys(SOURCE_META) as TimelineSource[];

function relative(ms: number, now: number): string {
  const d = Math.abs(now - ms);
  const future = ms > now;
  const unit =
    d < 60_000
      ? 'just now'
      : d < 3_600_000
        ? `${Math.round(d / 60_000)}m`
        : d < 86_400_000
          ? `${Math.round(d / 3_600_000)}h`
          : `${Math.round(d / 86_400_000)}d`;
  if (unit === 'just now') return unit;
  return future ? `in ${unit}` : `${unit} ago`;
}

function show(v: unknown): string {
  if (v === null || v === undefined) return '—';
  const s = String(v);
  return s.trim() === '' ? '—' : s;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  account: Account;
  canMutate: boolean;
}

export function RecordTimelinePanel({ account, canMutate }: Props) {
  const { push } = useToast();
  const [ledger, setLedger] = useState<ActivityEntry[] | null>(null);
  const [tasks, setTasks] = useState<ProjectTask[] | null>(null);
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [selected, setSelected] = useState<TimelineSource[]>([]);
  const [snapIdx, setSnapIdx] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const un1 = subscribeActivity(setLedger);
    const un2 = subscribeTasks(setTasks);
    const un3 = subscribeTickets(setTickets);
    return () => {
      un1();
      un2();
      un3();
    };
  }, []);

  const loading = ledger === null || tasks === null || tickets === null;
  const now = Date.now();
  const nameLc = account.name.toLowerCase();

  const fieldChanges = useMemo(
    () => deriveFieldChanges(account, ledger ?? []),
    [account, ledger],
  );

  const entries = useMemo(() => {
    const audit = account.audit ?? [];
    const isMeetingRow = (actor?: string) => (actor ?? '').startsWith('Google Meet');
    const isFieldRow = (text: string) => AUDIT_MOVE_RE.test(text) || AUDIT_STAGE_RE.test(text);
    return mergeTimeline(
      {
        emails: account.emails ?? [],
        meetings: audit
          .filter((a) => isMeetingRow(a.actor))
          .map((a) => ({ id: a.id, at: a.at, title: a.text, actor: a.actor })),
        tasks: (tasks ?? [])
          .filter((t) => t.accountId === account.id)
          .map((t) => ({
            id: t.id,
            at: t.dueDate ?? t.createdAt,
            title: t.title,
            status: t.status,
            assignee: t.assignee ?? undefined,
          })),
        tickets: (tickets ?? [])
          .filter(
            (t) =>
              t.title.toLowerCase().includes(nameLc) ||
              t.labels.some((l) => l.toLowerCase() === nameLc),
          )
          .map((t) => ({
            id: t.id,
            at: t.updatedAt ?? t.createdAt,
            title: t.title,
            status: t.status,
            owner: t.owner ?? undefined,
          })),
        notes: audit
          .filter((a) => !isMeetingRow(a.actor) && !isFieldRow(a.text))
          .map((a) => ({ id: a.id, at: a.at, text: a.text, actor: a.actor })),
        fieldChanges: fieldChanges.map((c, i) => ({ id: `fc-${i}-${c.at}`, ...c })),
        agentActions: (ledger ?? [])
          .filter((e) => AGENT_SOURCES.has(e.source) && e.summary.includes(account.name))
          .map((e) => ({
            id: e.id,
            at: e.at,
            summary: `${e.event}: ${e.summary}`,
            agentId: `argo (${e.source})`,
            provenance: { source: `ledger:${e.source}`, ledgerSeal: e.hash },
            refs: e.subjectId ? [{ type: 'approval', id: e.subjectId }] : undefined,
          })),
      },
      { recordId: account.id },
    );
  }, [account, ledger, tasks, tickets, fieldChanges, nameLc]);

  const visible = useMemo(() => filterTimeline(entries, selected), [entries, selected]);
  const groups = useMemo(() => groupByDay(visible), [visible]);
  const touched = lastTouch(entries);
  const upcoming = nextScheduled(entries, now);

  // --- Time Machine ---------------------------------------------------------
  const current = useMemo(() => accountState(account), [account]);
  const base = useMemo(() => baseStateOf(current, fieldChanges), [current, fieldChanges]);
  const snapshots = useMemo(() => snapshotsAt(fieldChanges), [fieldChanges]);
  const seals = sealCoverage(fieldChanges);

  // Slider position: 0 = before any change, i = at snapshots[i-1].
  const maxIdx = snapshots.length;
  const idx = Math.min(snapIdx ?? maxIdx, maxIdx);
  const targetTs = idx === 0 ? (snapshots[0] ?? 0) - 1 : snapshots[idx - 1];
  const target = useMemo(
    () => replayTo(base, fieldChanges, targetTs),
    [base, fieldChanges, targetTs],
  );
  const pendingDiff = useMemo(() => diffStates(current, target), [current, target]);
  const agentSplit = useMemo(
    () => agentOnlyRevert(current, fieldChanges, now),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [current, fieldChanges],
  );
  const agentDiff = useMemo(
    () => diffStates(current, agentSplit.resulting),
    [current, agentSplit],
  );

  const queuePlan = async (
    changes: { attr: string; from: unknown; to: unknown }[],
    label: string,
  ) => {
    const { actions, skipped } = planToActions(changes, account);
    if (actions.length === 0) {
      push({
        kind: 'info',
        text: 'Nothing to revert.',
        detail: skipped.length ? `Not restorable here: ${skipped.join(', ')}` : undefined,
      });
      return;
    }
    setBusy(true);
    try {
      for (const action of actions) {
        await queueApproval(action, 'chat', `${REVERSAL_NOTE_PREFIX}time-machine — ${label}`);
      }
      push({
        kind: 'success',
        text: `Queued ${actions.length} governed proposal${actions.length === 1 ? '' : 's'} — approve in the Dashboard inbox.`,
        detail: skipped.length ? `Skipped (not restorable): ${skipped.join(', ')}` : undefined,
      });
    } catch {
      push({ kind: 'error', text: 'Could not queue the revert proposals. Try again.' });
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = () =>
    queuePlan(
      revertPlan(current, target).changes,
      `restore state as of ${new Date(targetTs).toLocaleString()}`,
    );
  const handleAgentOnly = () => queuePlan(agentDiff, 'revert agent-authored changes only');

  // --- Render ----------------------------------------------------------------
  if (loading) {
    return (
      <p className="t-meta" style={{ padding: '12px 2px' }}>
        Loading unified timeline…
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header meta: last touch + next scheduled */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="t-meta" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Clock size={12} />
          {touched === null ? 'Never touched' : `Last touch ${relative(touched, now)}`}
        </span>
        {upcoming && (
          <span className="glass-pill glass-pill--green" title={new Date(upcoming.at).toLocaleString()}>
            Next: {upcoming.title} · {relative(upcoming.at, now)}
          </span>
        )}
      </div>

      {/* Per-source filter chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} role="group" aria-label="Filter timeline by source">
        {ALL_SOURCES.map((s) => {
          const meta = SOURCE_META[s];
          const Icon = meta.icon;
          const on = selected.includes(s);
          const count = entries.filter((e) => e.source === s).length;
          return (
            <button
              key={s}
              type="button"
              className="glass-pill"
              aria-pressed={on}
              onClick={() =>
                setSelected((prev) => (on ? prev.filter((x) => x !== s) : [...prev, s]))
              }
              style={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                opacity: selected.length === 0 || on ? 1 : 0.45,
                borderColor: on ? meta.tint : undefined,
              }}
            >
              <Icon size={11} />
              {meta.label}
              <span className="t-meta">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Unified stream, grouped by day */}
      {groups.length === 0 ? (
        <p className="t-meta">
          {entries.length === 0
            ? 'No activity on this record yet — emails, meetings, tasks, tickets, notes, field changes and agent actions will all land here.'
            : 'Nothing matches the selected sources.'}
        </p>
      ) : (
        groups.map((g) => (
          <section key={g.day}>
            <h4 className="t-section-title" style={{ margin: '0 0 8px' }}>
              {g.day}
            </h4>
            <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {g.entries.map((e) => (
                <TimelineRow key={e.id} entry={e} now={now} />
              ))}
            </ol>
          </section>
        ))
      )}

      {/* Time Machine */}
      <section
        style={{
          borderTop: '1px solid rgba(220, 226, 240, 0.14)',
          paddingTop: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <h4 className="t-section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          <History size={13} /> Time Machine
          <span className="glass-pill" title="Field changes covered by the tamper-evident Decision Ledger chain">
            <ShieldCheck size={11} /> {seals.sealed}/{seals.total} sealed
          </span>
        </h4>

        {snapshots.length === 0 ? (
          <p className="t-meta">
            No field-change history recorded for this account yet. Governed edits and workflow
            stage moves will appear here as scrubber stops.
          </p>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="range"
                min={0}
                max={maxIdx}
                step={1}
                value={idx}
                aria-label="Scrub record history"
                onChange={(ev) => setSnapIdx(Number(ev.target.value))}
                style={{ flex: 1 }}
              />
              <span className="t-meta" style={{ whiteSpace: 'nowrap' }}>
                {idx === 0 ? 'Before first change' : new Date(targetTs).toLocaleString()}
              </span>
            </div>

            {pendingDiff.length === 0 ? (
              <p className="t-meta">This is the record's current state — nothing to restore.</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {pendingDiff.map((d) => (
                  <li key={d.attr} className="t-meta">
                    {d.attr}: {show(d.from)} → <strong>{show(d.to)}</strong>
                  </li>
                ))}
              </ul>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="glass-btn glass-btn--primary"
                disabled={!canMutate || busy || pendingDiff.length === 0}
                onClick={handleRestore}
                title="Queue governed proposals restoring every field to this point in time"
              >
                <History size={13} /> Restore this state
              </button>
              <button
                type="button"
                className="glass-btn"
                disabled={!canMutate || busy || agentDiff.length === 0}
                onClick={handleAgentOnly}
                title={
                  agentDiff.length === 0
                    ? 'No agent-authored changes to revert'
                    : `Undo ${agentSplit.undo.length} agent change${agentSplit.undo.length === 1 ? '' : 's'} while keeping ${agentSplit.keep.length} human edit${agentSplit.keep.length === 1 ? '' : 's'}`
                }
              >
                <Undo2 size={13} /> Revert agent changes only
              </button>
              {!canMutate && <span className="t-meta">Viewers can scrub but not revert.</span>}
            </div>
            <p className="t-meta">
              Reverts never write directly — each one queues a governed proposal through the same
              approvals pipeline as every agent action.
            </p>
          </>
        )}
      </section>
    </div>
  );
}

/** One row of the unified stream, with provenance badges on agent work. */
function TimelineRow({ entry, now }: { entry: TimelineEntry; now: number }) {
  const meta = SOURCE_META[entry.source];
  const Icon = meta.icon;
  return (
    <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <span style={{ color: meta.tint, marginTop: 2 }} title={meta.label}>
        <Icon size={14} />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13 }}>{entry.title}</span>
          <span className="t-meta" title={new Date(entry.at).toLocaleString()}>
            {relative(entry.at, now)}
          </span>
        </div>
        {entry.body && (
          <div className="t-meta" style={{ marginTop: 2 }}>
            {entry.body}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
          <span
            className={`glass-pill ${entry.actor.kind === 'agent' ? 'glass-pill--green' : ''}`}
            title={entry.actor.kind === 'agent' ? 'Agent-authored' : 'Human-authored'}
          >
            {entry.actor.kind === 'agent' ? <Bot size={11} /> : null} {entry.actor.id}
          </span>
          {entry.provenance && (
            <span
              className="glass-pill glass-pill--blue"
              title="Provenance: where this came from, how confident, and its ledger seal"
            >
              {entry.provenance.source}
              {entry.provenance.confidence !== undefined &&
                ` · ${Math.round(entry.provenance.confidence * 100)}%`}
            </span>
          )}
          {entry.provenance?.ledgerSeal && (
            <span
              className="glass-pill"
              title={`Sealed into the tamper-evident Decision Ledger chain: ${entry.provenance.ledgerSeal}`}
            >
              <ShieldCheck size={11} /> {entry.provenance.ledgerSeal.slice(0, 15)}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}
